// src/components/user/AssignedAssets.jsx
import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Stack,
  Chip,
  Button,
  Skeleton,
  Divider,
  TextField,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
  InputAdornment,
} from "@mui/material";

// MUI Icons
import SearchIcon         from "@mui/icons-material/Search";
import ClearIcon          from "@mui/icons-material/Clear";
import QrCodeIcon         from "@mui/icons-material/QrCode2";
import CalendarTodayIcon  from "@mui/icons-material/CalendarToday";
import VisibilityIcon     from "@mui/icons-material/VisibilityOutlined";
import InboxIcon          from "@mui/icons-material/Inbox";
import WarningAmberIcon   from "@mui/icons-material/WarningAmber";

import { useAuth }                from "../../contexts/AuthContext";
import DataTable                  from "../../components/common/DataTable";
import AssetDetailsModal          from "../Modals/AssetDetailsModal";
import AssetQRModal               from "../Modals/AssetQRModal";
import { assignedAssetsService }  from "../../services/assignedAssetsService";

// ─── Status config ────────────────────────────────────────────────────────────
const STATUS_MAP = {
  assigned:            { color: "primary",  label: "Assigned"            },
  active:              { color: "success",  label: "Active"              },
  "in use":            { color: "success",  label: "In Use"              },
  available:           { color: "info",     label: "Available"           },
  maintenance:         { color: "warning",  label: "Maintenance"         },
  "in repair":         { color: "warning",  label: "In Repair"           },
  "under maintenance": { color: "warning",  label: "Under Maintenance"   },
  retired:             { color: "error",    label: "Retired"             },
  disposed:            { color: "error",    label: "Disposed"            },
  lost:                { color: "error",    label: "Lost"                },
  stolen:              { color: "error",    label: "Stolen"              },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency", currency: "INR", maximumFractionDigits: 0,
  }).format(amount || 0);

const formatDate = (date) => {
  if (!date) return "N/A";
  return new Date(date).toLocaleDateString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
  });
};

// ─── Skeleton ─────────────────────────────────────────────────────────────────
const AssignedAssetsSkeleton = () => (
  <Container
    maxWidth="xl"
    sx={{ mt: 4, mb: 4, display: "flex", flexDirection: "column", gap: 3 }}
  >
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <Box>
        <Skeleton variant="text" width={280} height={44} />
        <Skeleton variant="text" width={380} height={20} />
      </Box>
    </Box>

    <Box sx={{ borderRadius: 3, border: "1px solid #e0e0e0", background: "#fff", overflow: "hidden" }}>
      <Box sx={{ p: 2.5 }}>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Skeleton variant="rounded" width="66%" height={40} />
          <Skeleton variant="rounded" width="33%" height={40} />
        </Box>
      </Box>
      <Box sx={{ borderTop: "1px solid #e0e0e0" }}>
        {Array.from({ length: 6 }, (_, i) => (
          <Box key={i} sx={{
            display: "flex", alignItems: "center",
            px: 3, py: 2, borderBottom: "1px solid #f5f5f5", gap: 3,
          }}>
            <Skeleton variant="rounded" width={60}  height={18} />
            <Box sx={{ flex: 2 }}>
              <Skeleton variant="text"    width="70%" height={18} sx={{ mb: 0.5 }} />
              <Skeleton variant="text"    width="40%" height={14} />
            </Box>
            <Skeleton variant="rounded" width={80}  height={22} />
            <Skeleton variant="rounded" width={90}  height={22} />
            <Skeleton variant="rounded" width={70}  height={22} />
            <Box sx={{ display: "flex", gap: 0.5, ml: "auto" }}>
              <Skeleton variant="circular" width={32} height={32} />
              <Skeleton variant="circular" width={32} height={32} />
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  </Container>
);

// ─── Status Chip ──────────────────────────────────────────────────────────────
const StatusChip = ({ status }) => {
  const cfg = STATUS_MAP[status?.toLowerCase()] || { color: "default", label: status };
  return (
    <Chip
      label={cfg.label || status}
      size="small"
      color={cfg.color}
      sx={{ fontWeight: 600, fontSize: "0.7rem", textTransform: "capitalize" }}
    />
  );
};

// ─── Warranty Cell (Text Only) ────────────────────────────────────────────────
const WarrantyCell = ({ warrantyExpiry }) => {
  const now   = Date.now();
  const in30  = now + 30 * 24 * 60 * 60 * 1000;
  const expTs = warrantyExpiry ? new Date(warrantyExpiry).getTime() : null;

  const isExpired      = expTs && expTs < now;
  const isExpiringSoon = expTs && expTs > now && expTs < in30;

  const color = isExpired ? "error" : isExpiringSoon ? "warning" : expTs ? "success" : "default";
  const tag   = isExpired ? "Expired"  : isExpiringSoon ? "Expiring Soon"  : expTs ? "Valid" : "N/A";

  return (
    <Stack spacing={0}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: `${color}.main`, lineHeight: 1.3 }}>
        {tag}
      </Typography>
      <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.65rem" }}>
        {formatDate(warrantyExpiry)}
      </Typography>
    </Stack>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const AssignedAssets = () => {
  const { currentUser } = useAuth();

  // ── State ──────────────────────────────────────────────────────────────────
  const [assets,          setAssets]          = useState([]);
  const [userData,        setUserData]        = useState(null);
  const [loading,         setLoading]         = useState(true);
  const [alert,           setAlert]           = useState({ show: false, type: "error", message: "" });

  const [selectedAsset,   setSelectedAsset]   = useState(null);
  const [showDetails,     setShowDetails]     = useState(false);
  const [showQR,          setShowQR]          = useState(false);

  const [searchQuery,     setSearchQuery]     = useState("");

  // ── Data loading ───────────────────────────────────────────────────────────
  const loadData = async () => {
    if (!currentUser) return;
    try {
      setLoading(true);

      const { userProfile, assets: fetched } =
        await assignedAssetsService.getAssignedAssets(currentUser);

      setUserData(userProfile);
      setAssets(fetched);
    } catch (err) {
      console.error("Failed to fetch assigned assets:", err);
      setAlert({
        show:    true,
        type:    "error",
        message: err.code === "permission-denied"
          ? "Permission denied. Contact your administrator."
          : `Failed to load assets: ${err.message}`,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  // ── Filtered rows (Search Only) ────────────────────────────────────────────
  const filteredAssets = useMemo(() => {
    const term = searchQuery.trim().toLowerCase();

    return assets.filter((a) => {
      if (term) {
        const hay = [
          a.assetId, a.name, a.brand, a.model,
          a.serialNumber, a.location, a.category, a.status,
        ].filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });
  }, [assets, searchQuery]);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const openDetails = (asset) => { setSelectedAsset(asset); setShowDetails(true);  };
  const closeDetails= ()      => { setSelectedAsset(null);  setShowDetails(false); };
  const openQR      = (asset) => { setSelectedAsset(asset); setShowQR(true);       };
  const closeQR     = ()      => { setSelectedAsset(null);  setShowQR(false);      };

  // ── Table columns ──────────────────────────────────────────────────────────
  const columns = useMemo(() => [
    {
      header: "#",
      accessor: "slno",
      width: "52px",
      align: "center",
      render: (_, index) => (
        <Typography variant="caption" color="text.disabled" fontWeight={600}>
          {index + 1}
        </Typography>
      ),
    },
    {
      header: "Asset",
      accessor: "name",
      render: (row) => (
        <Stack spacing={0.35}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="body2" fontWeight={700} color="text.primary">
              {row.name}
            </Typography>
            {row.hasQRCode && (
              <Chip
                icon={<QrCodeIcon sx={{ fontSize: "0.75rem !important" }} />}
                label="QR"
                size="small"
                color="success"
                sx={{ height: 18, fontSize: "0.62rem", fontWeight: 700 }}
              />
            )}
          </Stack>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontFamily: "monospace" }}
          >
            {row.assetId}
          </Typography>
          <Typography variant="caption" color="text.disabled">
            {row.brand}
            {row.model && row.model !== "Not Specified" ? ` · ${row.model}` : ""}
          </Typography>
        </Stack>
      ),
    },
    {
      header: "Category",
      accessor: "category",
      render: (row) => (
        <Stack spacing={0.25}>
          <Chip
            label={row.category}
            size="small"
            color="default"
            variant="outlined"
            sx={{ fontWeight: 600, fontSize: "0.68rem", width: "fit-content" }}
          />
          {row.subCategory && (
            <Typography variant="caption" color="text.disabled">
              {row.subCategory}
            </Typography>
          )}
        </Stack>
      ),
    },
    {
      header: "Serial No.",
      accessor: "serialNumber",
      render: (row) => (
        <Typography
          variant="caption"
          sx={{
            fontFamily: "monospace",
            bgcolor: "action.hover",
            px: 1, py: 0.5,
            borderRadius: 1.5,
            display: "inline-block",
            color: "text.secondary",
          }}
        >
          {row.serialNumber}
        </Typography>
      ),
    },
    {
      header: "Location",
      accessor: "location",
      render: (row) => (
        <Stack spacing={0}>
          <Typography variant="caption" fontWeight={600} color="text.primary">
            {row.location}
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.65rem" }}>
            {[row.locationCity, row.locationState].filter(Boolean).join(", ") || "—"}
          </Typography>
        </Stack>
      ),
    },
    {
      header: "Status",
      accessor: "status",
      render: (row) => <StatusChip status={row.status} />,
    },
    {
      header: "Value",
      accessor: "purchaseValue",
      render: (row) => (
        <Stack spacing={0}>
          <Typography variant="body2" fontWeight={700}>
            {formatCurrency(row.purchaseValue)}
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.65rem" }}>
            {formatDate(row.purchaseDate)}
          </Typography>
        </Stack>
      ),
    },
    {
      header: "Warranty",
      accessor: "warrantyExpiry",
      render: (row) => <WarrantyCell warrantyExpiry={row.warrantyExpiry} />,
    },
    {
      header: "Assigned On",
      accessor: "assignedOn",
      render: (row) => (
        <Stack spacing={0}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <CalendarTodayIcon sx={{ fontSize: 12, color: "text.disabled" }} />
            <Typography variant="caption" color="text.primary" fontWeight={500}>
              {formatDate(row.assignedOn)}
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.65rem" }}>
            by {row.assignedBy}
          </Typography>
        </Stack>
      ),
    },
    {
      header: "Actions",
      accessor: "actions",
      align: "center",
      render: (row) => (
        <Stack direction="row" spacing={0.5} justifyContent="center">
          <Tooltip title="View Details" arrow>
            <IconButton
              size="small"
              onClick={() => openDetails(row)}
              sx={{
                color: "primary.main",
                "&:hover": { bgcolor: "primary.50" },
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {row.hasQRCode && (
            <Tooltip title="View QR Code" arrow>
              <IconButton
                size="small"
                onClick={() => openQR(row)}
                sx={{
                  color: "success.main",
                  "&:hover": { bgcolor: "success.50" },
                }}
              >
                <QrCodeIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      ),
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);

  // ── Guards ─────────────────────────────────────────────────────────────────
  if (loading) return <AssignedAssetsSkeleton />;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Container
      maxWidth="xl"
      sx={{ mt: 4, mb: 4, display: "flex", flexDirection: "column", gap: 3 }}
    >
      {/* ── Error Snackbar ──────────────────────────────────────────────── */}
      <Snackbar
        open={alert.show}
        autoHideDuration={6000}
        onClose={() => setAlert((p) => ({ ...p, show: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={alert.type}
          onClose={() => setAlert((p) => ({ ...p, show: false }))}
          sx={{ fontWeight: 500 }}
        >
          {alert.message}
        </Alert>
      </Snackbar>

      {/* ── Page Header ─────────────────────────────────────────────────── */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            component="h1"
            sx={{ fontWeight: 700, color: "text.primary", letterSpacing: "-0.5px" }}
          >
            My Assigned Assets
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Assets assigned to you by{" "}
            <strong>{userData?.company || "your company"}</strong>
          </Typography>
        </Box>
      </Box>

      {/* ── Empty state (no assets at all) ──────────────────────────────── */}
      {assets.length === 0 ? (
        <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
          <Box
            sx={{
              p: 8,
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <InboxIcon sx={{ fontSize: 56, color: "action.disabled" }} />
            <Typography variant="h6" color="text.secondary" fontWeight={600}>
              No Assets Assigned Yet
            </Typography>
            <Typography variant="body2" color="text.disabled">
              You currently have no assets assigned in{" "}
              {userData?.company || "your company"}. Assets assigned to you will
              appear here automatically.
            </Typography>
            <Chip
              icon={<WarningAmberIcon sx={{ fontSize: "1rem !important" }} />}
              label="Contact your administrator for asset assignment"
              color="primary"
              variant="outlined"
              sx={{ mt: 1, fontWeight: 500 }}
            />
          </Box>
        </Paper>
      ) : (
        /* ── Main Panel ─────────────────────────────────────────────────── */
        <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
          
          {/* Search bar */}
          <Box sx={{ p: 2.5 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  label="Search assets..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon
                          sx={{ color: "action.active" }}
                          fontSize="small"
                        />
                      </InputAdornment>
                    ),
                    endAdornment: searchQuery && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setSearchQuery("")}
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": { borderRadius: 2 },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent={{ xs: "flex-start", md: "flex-end" }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Showing{" "}
                    <strong>{filteredAssets.length}</strong>{" "}
                    of{" "}
                    <strong>{assets.length}</strong>{" "}
                    asset{assets.length !== 1 ? "s" : ""}
                    {searchQuery && (
                      <>
                        {" "}matching "<em>{searchQuery}</em>"
                      </>
                    )}
                  </Typography>
                  {searchQuery && (
                    <Button
                      size="small"
                      onClick={() => setSearchQuery("")}
                      sx={{
                        textTransform: "none",
                        fontSize: "0.75rem",
                        borderRadius: 1.5,
                        fontWeight: 600,
                      }}
                    >
                      Clear
                    </Button>
                  )}
                </Stack>
              </Grid>
            </Grid>
          </Box>

          <Divider />

          {/* Content — empty filtered state */}
          {filteredAssets.length === 0 ? (
            <Box
              sx={{
                p: 8,
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <InboxIcon sx={{ fontSize: 56, color: "action.disabled" }} />
              <Typography variant="h6" color="text.secondary" fontWeight={600}>
                No assets found
              </Typography>
              <Typography variant="body2" color="text.disabled">
                No assets match your current search.
              </Typography>
              <Button
                variant="contained"
                disableElevation
                onClick={() => setSearchQuery("")}
                sx={{
                  mt: 1,
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                Clear Search
              </Button>
            </Box>
          ) : (
            /* Data table */
            <Box sx={{ p: 0 }}>
              <DataTable
                columns={columns}
                data={filteredAssets}
                emptyMessage="No matching assets found."
                pageSize={10}
                searchable={false}
                exportable
                rowClassName={(row) => {
                  const parts = [];
                  if (["maintenance", "in repair", "under maintenance"]
                      .includes(row.status?.toLowerCase())) parts.push("maintenance-row");
                  if (row.warrantyExpiry && new Date(row.warrantyExpiry) < new Date())
                    parts.push("expired-warranty-row");
                  return parts.join(" ");
                }}
              />
            </Box>
          )}
        </Paper>
      )}

      {/* ── Modals ──────────────────────────────────────────────────────── */}
      {showDetails && selectedAsset && (
        <AssetDetailsModal
          asset={selectedAsset}
          onClose={closeDetails}
          userMode
        />
      )}
      {showQR && selectedAsset && (
        <AssetQRModal asset={selectedAsset} onClose={closeQR} />
      )}
    </Container>
  );
};

export default AssignedAssets;